import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "components/Modal/Modal";
import Button from "components/buttons/Button";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { closeModal } from "store/modals/slice";
import { ModalMainTextTabs, ModalTypes } from "configs/types";
import TextMainTab from "modals/cms/footer/mainText/tabs/TextMainTab";
import { TextLocalesTab } from "modals/cms/footer/mainText/tabs/TextLocalesTab";
import {
  getMainTextItem,
  handleClearDraftItem,
  updateMainTextItem,
  createMainTextItem,
} from "store/cms/footer/mainTexts/slice";
import { getChangedFields } from "helpers/getChangedFields";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import instance from "api/apiClient";
import { selectFilteredGeo } from "store/geoFilter/selectors";

import styles from "../mainText/CmsFooterMainTextEditModal.module.scss";
import TextTrailTab from "./tabs/TextTrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { normalizeSeoHtml } from "helpers/seoAttributes";

const CmsFooterMainTextEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeLocale, setActiveLocale] = useState<string>("");
  const [localeData, setLocaleData] = useState("");
  const [initialLocaleData, setInitialLocaleData] = useState("");
  const localesList = useAppSelector((state) => state.settings.locales);

  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const formErrors = useFormErrors();

  useEffect(() => {
    if (localesList?.length && !activeLocale) {
      setActiveLocale(localesList[0]);
    }
  }, [localesList, activeLocale]);

  const isItemFetched = useAppSelector(
    (store) => store["cms-main-texts"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["cms-main-texts"].isItemFetching,
  );

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getMainTextItem(+entityId ? +entityId : 0));
    }
  }, [isItemFetched, searchParams]);

  const tabs: ModalMainTextTabs[] = [
    ModalMainTextTabs.main,
    ModalMainTextTabs.local,
    ModalMainTextTabs.trail,
  ];
  const [activeTab, setActiveTab] = useState<ModalMainTextTabs>(
    ModalMainTextTabs.main,
  );
  const [isTabLoading, setIsTabLoading] = useState(false);
  const draftItem = useAppSelector(
    (state) => state["cms-main-texts"]?.draftItem,
  );
  const geoList = useAppSelector(selectFilteredGeo);

  const permissions = usePermissionHelper();
  const canSave = isEdit
    ? Boolean(permissions?.update)
    : Boolean(permissions?.create);

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const isLoaded = () => {
    if (entityId && !isItemFetched) return false;
    return true;
  };

  const loaded = isLoaded();

  const handleTabClick = (tab: ModalMainTextTabs) => {
    if (!isEdit && tab !== ModalMainTextTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
    setIsTabLoading(tab === ModalMainTextTabs.local);
  };

  const handleTabLoadingChange = useCallback((isLoading: boolean) => {
    setIsTabLoading(isLoading);
  }, []);

  const renderTab = () => {
    if (activeTab === ModalMainTextTabs.main) {
      return (
        <TextMainTab
          clearFieldError={formErrors.clearFieldError}
          fieldErrors={formErrors.errors}
          resetErrors={formErrors.resetErrors}
          readOnly={!canSave}
        />
      );
    }

    if (activeTab === ModalMainTextTabs.local) {
      return (
        <TextLocalesTab
          blockId={Number(entityId)}
          localesList={localesList}
          activeLocale={activeLocale}
          handleLocaleChange={handleLocaleChange}
          value={localeData}
          handleData={setLocaleData}
          onInitialData={setInitialLocaleData}
          clearFieldError={formErrors.clearFieldError}
          fieldErrors={formErrors.errors}
          resetErrors={formErrors.resetErrors}
          readOnly={!canSave}
          onLoadingChange={handleTabLoadingChange}
        />
      );
    }
    if (activeTab === ModalMainTextTabs.trail) {
      if (!entityId) return null;

      return <TextTrailTab entityId={entityId!} />;
    }

    return null;
  };

  const handleLocaleChange = async (newLocale: string) => {
    setActiveLocale(newLocale);
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    formErrors.resetErrors();

    if (activeTab === ModalMainTextTabs.local) {
      if (!draftItem.id) return;

      const textChanges = getChangedFields(
        { content: initialLocaleData },
        { content: localeData },
        ["content"],
        { content: normalizeSeoHtml },
      );
      if (!Object.keys(textChanges).length) return;

      try {
        await instance.put(
          `/cms/footer/main-text/set-translation/${draftItem.id}`,
          {
            locale: activeLocale,
            ...textChanges,
          },
        );
        setInitialLocaleData(localeData);

        dispatch(
          showNotification({
            title: t("success"),
            message: t("translationUpdate"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }

    const { id, isActive, geo, ...restData } = draftItem;

    if (!id) {
      try {
        const result = await dispatch(
          createMainTextItem({
            isActive: isActive ? 1 : 0,
            country: geo || geoList[0],
            ...restData,
          }),
        ).unwrap();

        navigate(
          `${location.pathname}?activeModal=${ModalTypes.mainText}&entityId=${result.id}`,
        );

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({ error, t, dispatch, formErrors });
      }

      return;
    }

    try {
      await dispatch(
        updateMainTextItem({
          id,
          isActive: isActive ? 1 : 0,
          country: geo,
          ...restData,
        }),
      ).unwrap();

      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header>{t("mainText")}</Modal.Header>
      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
            canBeClicked={isItemFetched}
          />

          <Modal.Content className={styles.cmsFooterMainTextWrapper}>
            {renderTab()}
          </Modal.Content>

          {activeTab !== ModalMainTextTabs.trail && canSave && (
            <Modal.Footer>
              {isTabLoading ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleClose}
                  />
                  <Button
                    isPrimary
                    isSecondary
                    handleClick={handleSave}
                    txt={t("save")}
                    isLoad={isItemFetching}
                  />
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default CmsFooterMainTextEditModal;
